import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  IGetNotificationsRequestInterfaces,
} from '@toolify/server/src/controllers/NotificationController/validators/GetNotifications/types/IGetNotificationsRequestInterfaces'
import {
  IGetNotificationRequestInterfaces,
} from '@toolify/server/src/controllers/NotificationController/validators/GetNotification/types/IGetNotificationRequestInterfaces'
import {NotificationType} from '@toolify/server/src/models/mongoose/NotificationModel/enum/NotificationType'
import {
  INotificationResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/INotificationResponseInstance'
import {mapNotificationResponseInstance} from '@toolify/client/src/modules/api/singletons/mapNotificationResponseInstance'

import {
  ISetAllUserNotificationsSeenRequestInterfaces,
} from '@toolify/server/src/controllers/NotificationController/validators/SetAllUserNotificationsSeen/types/ISetAllUserNotificationsSeenRequestInterfaces'
import {
  IUpdateNotificationRequestInterfaces,
} from '@toolify/server/src/controllers/NotificationController/validators/UpdateNotification/types/IUpdateNotificationRequestInterfaces'
import {ResourceType} from '@toolify/server/src/models/mongoose/PermissionModel/enum/ResourceType'
import {
  NotificationFilterType,
} from '@toolify/server/src/controllers/NotificationController/enum/NotificationFilterType'
import {
  IGetUnreadNotificationTypesCountRequestInterfaces,
} from '@toolify/server/src/controllers/NotificationController/validators/GetUnreadNotificationTypesCount/types/IGetUnreadNotificationTypesCountRequestInterfaces'
import {
  IGetNotificationGroupEventsRequestInterfaces,
} from '@toolify/server/src/controllers/NotificationController/validators/GetNotificationGroupEvents/types/IGetNotificationGroupEventsRequestInterfaces'
import {IToJson} from '@toolify/client/src/types/IToJson'
export const useNotificationApiStore = defineStore('notificationApi', {
  actions: {
    async getNotifications(
      notificationFilterType = NotificationFilterType.All,
      offset = 0,
      limit = 30,
      resourceType?: ResourceType,
      resourceId?: string,
    ): Promise<INotificationResponseInstance<NotificationType>[]> {
      const apiRequestStore = useApiRequestStore()
      const url = (resourceType && resourceId) ? `/resource/${resourceType}/${resourceId}/notifications` : '/notifications'
      const {notifications} = await apiRequestStore.get<IGetNotificationsRequestInterfaces>(url, {
        params: {
          notificationFilterType,
          offset,
          limit,
        },
      })
      return notifications.map(notification => {
        return mapNotificationResponseInstance(notification)
      })
    },

    async getNotification(notificationId: string): Promise<INotificationResponseInstance<NotificationType>> {
      const apiRequestStore = useApiRequestStore()
      const {notification} = await apiRequestStore.get<IGetNotificationRequestInterfaces>(`/notifications/${notificationId}`)
      return mapNotificationResponseInstance(notification)
    },

    async getUnreadNotificationTypesCount(
      resourceType?: ResourceType,
      resourceId?: string,
    ): Promise<IGetUnreadNotificationTypesCountRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const url = (resourceType && resourceId) ? `/resource/${resourceType}/${resourceId}/notifications/unread/count` : '/notifications/unread/count'
      const response = await apiRequestStore.get<IGetUnreadNotificationTypesCountRequestInterfaces>(url)
      return response
    },

    async setAllUserNotificationsSeen(
      resourceType?: ResourceType,
      resourceId?: string,
    ): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      const url = (resourceType && resourceId) ? `/resource/${resourceType}/${resourceId}/notifications/seen` : '/notifications/seen'
      await apiRequestStore.post<ISetAllUserNotificationsSeenRequestInterfaces>(url)
    },

    async setUserNotificationsSeen(notificationIds: string[]): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.post<ISetAllUserNotificationsSeenRequestInterfaces>('/notifications/seen', {
        data: {
          notificationIds,
        },
      })
    },

    async setAllUserNotificationsRead(
      notificationFilterType = NotificationFilterType.All,
      resourceType?: ResourceType,
      resourceId?: string,
    ): Promise<number> {
      const apiRequestStore = useApiRequestStore()
      const url = (resourceType && resourceId) ? `/resource/${resourceType}/${resourceId}/notifications/read` : '/notifications/read'
      const {readNotificationsCount} = await apiRequestStore.post<ISetAllUserNotificationsSeenRequestInterfaces>(url, {
        data: {
          notificationFilterType,
        },
      })
      return readNotificationsCount
    },

    async markNotificationAsRead(notificationId: string): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.patch<IUpdateNotificationRequestInterfaces>(`/notifications/${notificationId}`, {
        data: {
          readAt: new Date(),
        },
      })
    },
    async markNotificationAsUnread(notificationId: string): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.patch<IUpdateNotificationRequestInterfaces>(`/notifications/${notificationId}`, {
        data: {
          readAt: null,
        },
      })
    },
    async getNotificationGroupEvents(
      notificationId: string,
      offset = 0,
      limit = 50,
    ): Promise<IToJson<IGetNotificationGroupEventsRequestInterfaces['response']>['events']> {
      const apiRequestStore = useApiRequestStore()
      const {events} = await apiRequestStore.get<IGetNotificationGroupEventsRequestInterfaces>(
        `/notifications/${notificationId}/events`,
        {
          params: {
            offset,
            limit,
          },
        },
      )
      return events as IToJson<IGetNotificationGroupEventsRequestInterfaces['response']>['events']
    },

    async markNotificationGroupAsRead(notificationId: string): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.patch<IUpdateNotificationRequestInterfaces>(`/notifications/${notificationId}/group`, {
        data: {
          readAt: new Date(),
        },
      })
    },
  },
})
